import {ObjectId} from 'bson';
import {z} from 'zod';
import {zodTypeguard} from './api/helpers';

export const TaskTypeSchema = z.enum([
  // customer facing
  'accountEnrichment',
  'accountEnrichmentAnalysis',
  'airtableCRMImport',
  'exportCsv',
  'exportToClay',
  'fileCRMImport',
  'generateAnalystAgent',
  'generateCampaigns',
  'generateMarketReport',
  'generateSegmentationAgent',
  'generateSegments',
  'generateTeamSizeAgent',
  'hubSpotEnrich',
  'hubSpotPushSavedAccounts',
  'hubSpotPullAndSync',
  'hubSpotReverseEnrich',
  'hubSpotFullRun',
  'importLinkedInAdAccounts',
  'importLinkedInIntentFields',
  'recommendedAccountsImport',
  'refreshAccounts',
  'salesforceEnrich',
  'salesforcePushSavedAccounts',
  'salesforcePullAndSync',
  'salesforceReverseEnrich',
  'salesforceFullRun',
  'syncAirtableAccounts',
  'validateTestList',

  // internal
  'cleanupTasks',
  'createUpworkImportTrello',
  'crmSyncCustomers',
  'deleteStaleAccounts',
  'importLinkedInAdAccountsForAllCustomers',
  'importLinkedInFieldsForAllCustomers',
  'processSlackPolls',
  'refreshCustomerLimits',
  'refreshCustomers',
  'refreshScoringData',
  'runAgentEnrichments',
  'sendDowngradeEmails',
  'setupWebIntentSources',
  'syncOverrides',
  'syncRedirectReviews',
  'testTask',
  'updateStaleAccounts',
]);
export type TaskType = z.infer<typeof TaskTypeSchema>;
export const TaskTypes = TaskTypeSchema.options;

const TaskStateSchema = z.enum([
  'pending',
  'running',
  'done',
  'failed',
  'canceled',
]);
export type TaskState = z.infer<typeof TaskStateSchema>;

// Detailed task state needs to be serializable into something that can be
// written to and read from mongo. TODO: is there a better type for this?
export type SerializableRecord = {
  [key: string]:
    | number
    | string
    | boolean
    | ObjectId
    | Date
    | null
    | undefined
    | (number | string | boolean | ObjectId | Date | SerializableRecord)[]
    | SerializableRecord;
};

export const SerializableRecordSchema: z.ZodType<SerializableRecord> = z.lazy(
  () =>
    z.record(
      z.union([
        z.string(),
        z.number(),
        z.boolean(),
        z.instanceof(ObjectId),
        z.date(),
        z.null(),
        z.undefined(),
        z.array(
          z.union([
            z.string(),
            z.number(),
            z.boolean(),
            z.instanceof(ObjectId),
            z.date(),
            SerializableRecordSchema,
          ])
        ),
        SerializableRecordSchema,
      ])
    )
);

const FetchStatusSchema = z.object({
  fetchErrors: z.number(),
  otherErrors: z.number(),
  ready: z.number(),
});
export const isFetchStatus = zodTypeguard(FetchStatusSchema);
export type FetchStatus = z.infer<typeof FetchStatusSchema>;

const TaskUpdateSchema = z.object({
  timestamp: z.date(),
  state: TaskStateSchema.optional(),
  error: z.string().optional(),
  detailedState: SerializableRecordSchema.optional(),
  fetchStatus: FetchStatusSchema.optional(),
});
export type TaskUpdate = z.infer<typeof TaskUpdateSchema>;

const InitiatedByUserSchema = z.object({
  type: z.literal('user'),
  userId: z.string(),
});

const InitiatedByTaskSchema = z.object({
  type: z.literal('task'),
  taskId: z.instanceof(ObjectId),
});

const InitiatedByScriptSchema = z.object({
  type: z.literal('script'),
});

const InitiatedByCloudSchedulerSchema = z.object({
  type: z.literal('cloudScheduler'),
});

const InitiatedBySystemSchema = z.object({
  type: z.literal('system'),
});

const InitiatedByAirtableSchema = z.object({
  type: z.literal('airtable'),
});

// tasks that were created before we added the InitiatedBy type
const MissingInitiatedBySchema = z.object({
  type: z.literal('missing'),
});

export const InitiatedBySchema = z.discriminatedUnion('type', [
  InitiatedByUserSchema,
  InitiatedByTaskSchema,
  InitiatedByScriptSchema,
  InitiatedByCloudSchedulerSchema,
  InitiatedBySystemSchema,
  InitiatedByAirtableSchema,
  MissingInitiatedBySchema,
]);
export type InitiatedBy = z.infer<typeof InitiatedBySchema>;

const TaskSchema = z.object({
  timestamp: z.date(),
  fullName: z.string(),
  name: z.string(),
  type: TaskTypeSchema,
  initiatedBy: InitiatedBySchema,

  // These fields are shortcuts so we don't have to look through updates for
  // the most recent write to a given field.
  state: TaskStateSchema,
  fetchStatus: FetchStatusSchema.optional(),
  error: z.string().optional(),

  pendingCancel: z.boolean().optional(),
  // only one task with this ID can run at a time
  concurrencyConstraint: z.string().optional(),
  // only one task with this ID can be pending or running at a time
  existenceConstraint: z.string().optional(),

  setupOutput: SerializableRecordSchema.optional(),
  updates: z.array(TaskUpdateSchema).optional(),

  // sanity check to ensure task isn't executed multiple times in parallel
  currentlyExecuting: z.boolean().optional(),

  // Store additional data that might be useful for internal use cases
  debug: z
    .object({
      // TODO: convert Customer type to zod schema
      customer: z
        .object({
          name: z.string(),
        })
        .optional(),
    })
    .optional(),
});

// Helper function to create a task schema with a detailed state.
// - https://stackoverflow.com/questions/74907523/creating-zod-schema-for-generic-interface
// - https://github.com/colinhacks/zod#writing-generic-functions
export const createTaskSchema = <
  DetailedStateSchema extends z.ZodType<SerializableRecord>,
  InputSchema extends z.ZodType<Record<string, unknown>>, // TODO: constrain to SerializableRecord
>({
  detailedStateSchema,
  inputSchema,
}: {
  detailedStateSchema: DetailedStateSchema;
  inputSchema: InputSchema;
}) => {
  return TaskSchema.extend({
    detailedState: detailedStateSchema.optional(),
    input: inputSchema,
  });
};

export const GenericTaskSchema = createTaskSchema({
  inputSchema: z.record(z.unknown()),
  detailedStateSchema: SerializableRecordSchema,
});
export type GenericTask = z.infer<typeof GenericTaskSchema>;

export type TaskRunnerResult<
  T extends SerializableRecord = SerializableRecord,
> = {
  complete: boolean;
  newState?: T;
  fetchStatus?: FetchStatus;
};
